/* =========================================================================
   RbacContext — exposes the current user's effective permissions to the whole
   app for gating, plus a shared roles cache so the management screens don't
   each re-fetch.

   Gating usage:
     const { can } = useRbac();
     if (can("content", "publish")) { ... }

     <Can module="users" action="create">
       <button>Add user</button>
     </Can>

   NOTE: client-side gating is for UX only (hide what a user can't use). Real
   enforcement happens on the backend (requirePermission middleware). The map
   below is seeded from localStorage (instant, no flicker) and then refreshed
   from GET /rbac/me on load so it can't go stale after an admin edits the
   caller's role.
   ========================================================================= */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { listRoles, fetchMyPermissions } from "./rbacService";
import {
  fullPermissions,
  emptyPermissions,
  normalizePermissions,
} from "./permissions";

// The initial permission map for UI gating.
//
// The stored "permissions" map is the source of truth — the backend computes it
// per user at login (full for a super-admin, limited for staff), so we must
// honour it. Both super-admins and staff carry the "Admin" role string, so the
// role string alone can NOT distinguish them; the permission map can.
//
// Only when NO permissions map exists at all (e.g. a legacy admin session from
// before this shipped) do we fall back by role: "Admin" → full access so we
// never lock the top-level admin out; anyone else → no access.
const readCachedPermissions = () => {
  try {
    const raw = localStorage.getItem("permissions");
    if (raw) return normalizePermissions(JSON.parse(raw));
  } catch {
    /* fall through to role-based fallback */
  }
  const role = localStorage.getItem("role");
  if (role === "Admin") return fullPermissions();
  return emptyPermissions();
};

const RbacContext = createContext(null);

export const RbacProvider = ({ children }) => {
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);

  const refreshRoles = useCallback(async () => {
    // No session yet (e.g. the login screen) → don't hit the authed /rbac/roles
    // endpoint. It would 401 and is pointless before login.
    if (!localStorage.getItem("token")) {
      setRoles([]);
      setRolesLoading(false);
      return [];
    }
    setRolesLoading(true);
    try {
      const data = await listRoles();
      setRoles(data);
      return data;
    } catch {
      // Don't let a failed roles fetch bubble up as an unhandled rejection.
      setRoles([]);
      return [];
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshRoles();
  }, [refreshRoles]);

  // Seed from the localStorage cache for an instant first paint…
  const [effectivePermissions, setEffectivePermissions] = useState(
    readCachedPermissions
  );

  // Refresh the effective permissions from the server so the UI reflects any
  // role change since login (or fills in a login write that omitted/under-set
  // "permissions"). The backend returns the correct map per user (full for a
  // super-admin or real Creator, limited for staff), so this is safe for all
  // roles. Only attempt when there is a session (a token).
  // Guards against the mount effect and a same-tick "storage" event both
  // firing (e.g. another tab logging in/out right as this one loads) from
  // issuing two concurrent /rbac/me requests.
  const refreshInFlightRef = useRef(false);
  const refreshPermissions = useCallback(async () => {
    if (!localStorage.getItem("token") || refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    try {
      const fresh = await fetchMyPermissions();
      if (!fresh) return;
      const normalized = normalizePermissions(fresh);
      setEffectivePermissions(normalized);
      try {
        localStorage.setItem("permissions", JSON.stringify(normalized));
      } catch {
        /* ignore quota errors */
      }
    } finally {
      refreshInFlightRef.current = false;
    }
  }, []);

  // RbacProvider mounts once and wraps the whole app — including the login page,
  // where localStorage has no permissions yet. When the user logs in, Login.js
  // writes permissions/role/token and fires a "storage" event; re-read here so
  // the sidebar/gating populate immediately WITHOUT needing a manual reload.
  //
  // Also kick off a server refresh right here (not just on the one-time mount
  // effect below): RbacProvider mounts once above <Routes>, so its mount-time
  // refresh runs on the public login page before a token exists and never
  // fires again after a same-tab login. Without this, the sidebar depends on
  // the login response's "permissions" being complete/correct at write time —
  // any gap there (e.g. a login path that doesn't set it) silently shows a
  // truncated menu until the next hard reload. Refreshing from /rbac/me here
  // makes every same-tab login self-heal immediately instead.
  useEffect(() => {
    const onStorage = () => {
      setEffectivePermissions(readCachedPermissions());
      if (localStorage.getItem("token")) {
        refreshRoles();
        refreshPermissions();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [refreshRoles, refreshPermissions]);

  // …then refresh from the server on load too, for the hard-reload case (no
  // "storage" event fires then — the cache is just read synchronously above).
  useEffect(() => {
    refreshPermissions();
  }, [refreshPermissions]);

  const can = useCallback(
    (moduleKey, action) => Boolean(effectivePermissions?.[moduleKey]?.[action]),
    [effectivePermissions]
  );

  const value = useMemo(
    () => ({
      roles,
      rolesLoading,
      refreshRoles,
      effectivePermissions,
      can,
    }),
    [roles, rolesLoading, refreshRoles, effectivePermissions, can]
  );

  return <RbacContext.Provider value={value}>{children}</RbacContext.Provider>;
};

export const useRbac = () => {
  const ctx = useContext(RbacContext);
  if (!ctx) throw new Error("useRbac must be used within an RbacProvider");
  return ctx;
};

/** Conditional render gate. Renders children only if the current user can do
 *  `action` on `module`; otherwise renders `fallback` (default nothing). */
export const Can = ({ module, action, children, fallback = null }) => {
  const { can } = useRbac();
  return can(module, action) ? children : fallback;
};
